import { useEffect, useState } from 'react';

/**
 * True on viewports ≤ 767px (the Tailwind `md` breakpoint boundary). Used to
 * gate the master/detail split: on mobile the detail opens as a full-screen
 * Drawer overlay instead of stacking below a long list (which forces a scroll
 * to the bottom and back). Shared by the Mandates + Resolution views.
 *
 * SSR-safe: returns false until mounted (no `window`/`matchMedia`), then syncs.
 */
export function useIsMobile(maxWidth = 767): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = (): void => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [maxWidth]);
  return isMobile;
}

/**
 * True below the Tailwind `lg` boundary (< 1024px). The Mandates
 * master/detail split uses it: between 768 and 1023px a 640px side panel
 * left the list column ~56px wide, so the detail opens as the drawer there
 * too (labels-review/ux.md §10.1).
 */
export function useIsBelowLg(): boolean {
  return useIsMobile(1023);
}
